import React, { useState } from 'react';
import { Package, Download, Eye, Trash2, ImageOff, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const dash = <span className="text-slate-400">—</span>;
const weight = (v) => (v ? `${v} g` : '');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : '');

export const EAN_EXPORT_COLUMNS = [
  ['Product', (r) => r.product_name], ['Flavour', (r) => r.flavour], ['Brand', (r) => r.brand_name],
  ['Description', (r) => r.product_description], ['MRP', (r) => r.mrp], ['HSN', (r) => r.hsn_code],
  ['Tax Rate', (r) => r.tax_rate], ['Gross Weight (g)', (r) => r.gross_weight_grams],
  ['Net Weight (g)', (r) => r.net_weight_grams], ['Country of Origin', (r) => r.country_of_origin],
  ['Storage Condition', (r) => r.storage_condition], ['Status', (r) => r.status], ['EAN', (r) => r.ean_number],
  ['SKU', (r) => r.assigned_sku], ['Submitted By', (r) => r.submitted_by], ['Submitted', (r) => fmtDate(r.submitted_at)],
  ['Updated', (r) => fmtDate(r.updated_at)],
];

export const exportEanCsv = (rows, tab) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [EAN_EXPORT_COLUMNS.map(([h]) => esc(h)).join(','),
    ...rows.map((r) => EAN_EXPORT_COLUMNS.map(([, f]) => esc(f(r))).join(','))].join('\r\n');
  const url = URL.createObjectURL(new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: `ean-requests-${tab}-${new Date().toISOString().slice(0, 10)}.csv` });
  a.click();
  URL.revokeObjectURL(url);
};

const Thumb = ({ src, label, onOpen, testId }) => (src ? (
  <button type="button" onClick={() => onOpen({ src, label })} data-testid={testId}
    className="h-10 w-10 overflow-hidden rounded-md border border-slate-200 bg-white transition-transform hover:scale-110">
    <img src={src} alt={label} className="h-full w-full object-cover" />
  </button>
) : <ImageOff className="h-4 w-4 text-slate-300" />);

const Clip = ({ text, width = 'max-w-[220px]' }) => (text
  ? <span className={`block ${width} truncate`} title={text}>{text}</span> : dash);

const PIN_LEFT = 'sticky left-0 z-10 bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.08)]';
const PIN_RIGHT = 'sticky right-0 z-10 bg-white shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.08)]';
const HEADS = ['Brand', 'Description', 'MRP', 'HSN', 'Tax Rate', 'Gross Wt', 'Net Wt', 'Country of Origin',
  'Storage Condition', 'Front', 'Back', 'Status', 'EAN', 'SKU', 'Barcode', 'Submitted By', 'Submitted'];

export const EANRequestsTable = ({ requests, getStatusBadge, nameFromEmail, onView, onEdit, canEdit, onDelete, canDelete, onDownloadBarcode }) => {
  const [preview, setPreview] = useState(null);
  return (
    <>
      <Table className="whitespace-nowrap" data-testid="ean-requests-table">
        <TableHeader>
          <TableRow>
            <TableHead className={PIN_LEFT}>Product</TableHead>
            {HEADS.map((h) => <TableHead key={h}>{h}</TableHead>)}
            <TableHead className={`${PIN_RIGHT} text-right`}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.map((req) => (
            <TableRow key={req.id} className="group hover:bg-slate-50" data-testid={`ean-row-${req.id}`}>
              <TableCell className={`${PIN_LEFT} font-medium group-hover:bg-slate-50`}>
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-slate-400" />
                  <div>
                    <p className="text-slate-900">{req.product_name}</p>
                    {req.flavour && <p className="text-xs text-slate-500">{req.flavour}</p>}
                  </div>
                </div>
              </TableCell>
              <TableCell>{req.brand_name}</TableCell>
              <TableCell className="text-sm text-slate-600"><Clip text={req.product_description} /></TableCell>
              <TableCell>₹{req.mrp}</TableCell>
              <TableCell className="font-mono text-sm">{req.hsn_code}</TableCell>
              <TableCell>{req.tax_rate || dash}</TableCell>
              <TableCell>{weight(req.gross_weight_grams) || dash}</TableCell>
              <TableCell>{weight(req.net_weight_grams) || dash}</TableCell>
              <TableCell>{req.country_of_origin || dash}</TableCell>
              <TableCell className="text-sm text-slate-600"><Clip text={req.storage_condition} width="max-w-[180px]" /></TableCell>
              <TableCell><Thumb src={req.front_image} label={`${req.product_name} — Front`} onOpen={setPreview} testId={`ean-front-thumb-${req.id}`} /></TableCell>
              <TableCell><Thumb src={req.back_image} label={`${req.product_name} — Back`} onOpen={setPreview} testId={`ean-back-thumb-${req.id}`} /></TableCell>
              <TableCell>{getStatusBadge(req.status)}</TableCell>
              <TableCell>{req.ean_number ? <span className="font-mono text-sm text-green-600">{req.ean_number}</span> : dash}</TableCell>
              <TableCell>{req.assigned_sku ? <span className="font-mono text-xs text-blue-600">{req.assigned_sku}</span> : dash}</TableCell>
              <TableCell>
                {req.barcode_image ? (
                  <Button variant="ghost" size="sm" onClick={() => onDownloadBarcode(req)}
                    className="h-7 px-2 text-[#2EB4AC] hover:text-[#269d96] hover:bg-[#2EB4AC]/10" data-testid={`download-barcode-${req.id}`}>
                    <Download className="h-3.5 w-3.5 mr-1" /><span className="text-xs">Download</span>
                  </Button>
                ) : dash}
              </TableCell>
              <TableCell className="text-sm">
                <div className="flex flex-col leading-tight">
                  <span className="text-slate-900">{nameFromEmail(req.submitted_by)}</span>
                  <span className="text-xs text-slate-400 truncate max-w-[160px]" title={req.submitted_by}>{req.submitted_by}</span>
                </div>
              </TableCell>
              <TableCell className="text-sm text-slate-500">{fmtDate(req.submitted_at)}</TableCell>
              <TableCell className={`${PIN_RIGHT} text-right group-hover:bg-slate-50`}>
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => onView(req)} className="h-8 w-8 p-0" data-testid={`view-request-${req.id}`}>
                    <Eye className="h-4 w-4 text-slate-500" />
                  </Button>
                  {canEdit(req) && (
                    <Button variant="ghost" size="sm" onClick={() => onEdit(req)} className="h-8 w-8 p-0 text-[#2EB4AC] hover:text-[#269d96]" title="Edit (only while Pending)" data-testid={`edit-request-${req.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {req.status === 'pending' && canDelete(req) && (
                    <Button variant="ghost" size="sm" onClick={() => onDelete(req.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700" data-testid={`delete-request-${req.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl" data-testid="ean-image-preview">
          <DialogHeader><DialogTitle>{preview?.label}</DialogTitle></DialogHeader>
          {preview && <img src={preview.src} alt={preview.label} className="max-h-[70vh] w-full rounded-lg object-contain bg-slate-50" />}
        </DialogContent>
      </Dialog>
    </>
  );
};
